import { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { FileText, CreditCard, CheckCircle, AlertCircle, Loader2, Landmark, Banknote, Receipt } from "lucide-react";

const PublicPaymentPage = () => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  useEffect(() => {
    const fetchInvoice = async () => {
      try {
        const response = await axios.get(`${API}/pay/${token}`);
        setInvoice(response.data);
        
        // Check for payment status
        if (response.data.status === 'paid') {
          setPaymentSuccess(true);
        }
      } catch (error) {
        setError("This payment link is invalid or has expired.");
      } finally {
        setLoading(false);
      }
    };

    fetchInvoice();
  }, [token]);

  // Handle Stripe return
  useEffect(() => {
    const paymentStatus = searchParams.get('payment');
    const sessionId = searchParams.get('session_id');

    if (paymentStatus === 'success' && sessionId) {
      setPaymentSuccess(true);
      // Refresh invoice data
      axios.get(`${API}/pay/${token}`)
        .then(response => setInvoice(response.data))
        .catch(() => {});
    } else if (paymentStatus === 'cancelled') {
      toast.info("Payment was cancelled");
    }
  }, [searchParams]);

  const handlePayWithCard = async () => {
    setProcessing(true);
    try {
      const response = await axios.post(`${API}/pay/${token}/checkout`, {
        origin_url: window.location.origin
      });
      
      // Redirect to Stripe
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start payment");
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading invoice...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4">
        <div className="bg-white border border-zinc-200 p-8 max-w-md w-full text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h1 className="font-outfit text-xl font-medium text-zinc-900 mb-2">Invalid Payment Link</h1>
          <p className="text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  if (paymentSuccess || (invoice && invoice.balance_due <= 0)) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4">
        <div className="bg-white border border-zinc-200 p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-emerald-100 flex items-center justify-center mx-auto mb-4 rounded-full">
            <CheckCircle className="w-8 h-8 text-emerald-600" />
          </div>
          <h1 className="font-outfit text-2xl font-medium text-zinc-900 mb-2">Payment Complete</h1>
          <p className="text-muted-foreground mb-6">
            Thank you for your payment. Invoice #{invoice?.invoice_number} has been paid.
          </p>
          <div className="bg-emerald-50 border border-emerald-200 p-4 text-left">
            <div className="flex justify-between mb-2">
              <span className="text-sm text-muted-foreground">Amount Paid</span>
              <span className="font-medium text-emerald-700">${invoice?.grand_total?.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Status</span>
              <span className="text-emerald-700 font-medium">Paid</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4">
      <div className="bg-white border border-zinc-200 max-w-lg w-full">
        {/* Header */}
        <div className="bg-primary p-6 text-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 flex items-center justify-center">
              <FileText className="w-6 h-6" />
            </div>
            <div>
              <h1 className="font-outfit text-xl font-medium">Pay Invoice</h1>
              <p className="text-sm text-primary-foreground/80">{invoice?.organization?.name}</p>
            </div>
          </div>
        </div>

        {/* Invoice Details */}
        <div className="p-6 space-y-6">
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Invoice Number</span>
              <span className="font-medium">{invoice?.invoice_number}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Invoice Date</span>
              <span>{invoice?.invoice_date}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Due Date</span>
              <span>{invoice?.due_date}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Customer</span>
              <span>{invoice?.customer?.name}</span>
            </div>
          </div>

          <div className="border-t border-zinc-200 pt-4">
            <div className="flex justify-between text-lg">
              <span className="font-medium">Amount Due</span>
              <span className="font-outfit font-semibold text-primary text-2xl">
                ${invoice?.balance_due?.toFixed(2)}
              </span>
            </div>
          </div>

          {/* Payment Options */}
          <div className="space-y-3">
            <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wider">Payment Options</h3>
            
            {/* Card Payment via Stripe */}
            <Button
              onClick={handlePayWithCard}
              disabled={processing}
              className="w-full gap-3 h-14 text-base bg-[#635bff] hover:bg-[#5249e0]"
              data-testid="pay-with-card-btn"
            >
              {processing ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <CreditCard className="w-5 h-5" />
              )}
              {processing ? "Processing..." : "Pay with Credit/Debit Card"}
            </Button>

            <p className="text-xs text-center text-muted-foreground">
              Secure payment powered by Stripe
            </p>
          </div>

          {/* Other Payment Options */}
          <div className="space-y-3">
            <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wider">Other Payment Methods</h3>
            
            <div className="border border-zinc-200 divide-y divide-zinc-200">
              <div className="flex items-center gap-3 p-4">
                <div className="w-10 h-10 bg-emerald-50 flex items-center justify-center rounded-lg shrink-0">
                  <Banknote className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="font-medium text-sm">Cash</p>
                  <p className="text-xs text-muted-foreground">Pay in person at our office</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-4">
                <div className="w-10 h-10 bg-blue-50 flex items-center justify-center rounded-lg shrink-0">
                  <Receipt className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium text-sm">Check</p>
                  <p className="text-xs text-muted-foreground">Mail a check to our business address</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-4">
                <div className="w-10 h-10 bg-violet-50 flex items-center justify-center rounded-lg shrink-0">
                  <Landmark className="w-5 h-5 text-violet-600" />
                </div>
                <div>
                  <p className="font-medium text-sm">ACH Bank Transfer</p>
                  <p className="text-xs text-muted-foreground">Direct bank-to-bank transfer</p>
                </div>
              </div>
            </div>

            <div className="bg-zinc-50 p-3 border border-zinc-200 text-sm text-muted-foreground">
              <p className="font-medium text-zinc-700 mb-1">Contact us to arrange payment:</p>
              {invoice?.organization?.email && (
                <p>Email: <a href={`mailto:${invoice.organization.email}`} className="text-primary underline">{invoice.organization.email}</a></p>
              )}
              {invoice?.organization?.phone && (
                <p>Phone: <a href={`tel:${invoice.organization.phone}`} className="text-primary underline">{invoice.organization.phone}</a></p>
              )}
              {!invoice?.organization?.email && !invoice?.organization?.phone && (
                <p>Please contact {invoice?.organization?.name} directly.</p>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-zinc-200 p-4 bg-zinc-50 text-center">
          <p className="text-xs text-muted-foreground">
            Questions? Contact {invoice?.organization?.name}
          </p>
        </div>
      </div>
    </div>
  );
};

export default PublicPaymentPage;
